/**
 * TaskCard - Card for the Tasks list.
 *
 * Displays title with priority badge, description excerpt, metadata row
 * (schedule date, linked issue count, assignee), status badge, and action buttons.
 * Supports optional batch-selection checkbox.
 */

import type { Task } from '@/types/tasks';
import { PRIORITY_COLORS, STATUS_COLORS } from '@/types/tasks';

interface TaskCardProps {
  task: Task;
  batchMode?: boolean;
  isSelected?: boolean;
  onEdit: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onEmail?: () => void;
  onToggleSelect?: () => void;
}

/** Truncate to N chars with ellipsis. */
function excerpt(text: string, max = 80): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '\u2026';
}

/** Format ISO date to short locale string. */
function shortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const TERMINAL: Set<string> = new Set(['Done', 'Canceled']);

export default function TaskCard({
  task,
  batchMode = false,
  isSelected = false,
  onEdit,
  onComplete,
  onCancel,
  onEmail,
  onToggleSelect,
}: TaskCardProps) {
  const prioColor = PRIORITY_COLORS[task.priority];
  const statusColor = STATUS_COLORS[task.status];
  const isTerminal = TERMINAL.has(task.status);
  const scheduleDate = task.schedule.start;
  const assigneeName = task.assignees[0]?.userId;

  return (
    <div className={`tp-task-card${isSelected ? ' tp-task-card--selected' : ''}`}>
      {/* Batch checkbox */}
      {batchMode && (
        <label className="tp-task-card__checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
          />
        </label>
      )}

      {/* Body */}
      <div className="tp-task-card__body">
        {/* Row 1: Title + priority */}
        <div className="tp-task-card__title-row">
          <span
            className="tp-badge"
            style={{ background: prioColor, color: '#fff' }}
          >
            {task.priority}
          </span>
          <span className="tp-task-card__title">{task.title}</span>
        </div>

        {/* Row 2: Description excerpt */}
        {task.description && (
          <div className="tp-task-card__desc">{excerpt(task.description)}</div>
        )}

        {/* Row 3: Metadata */}
        <div className="tp-task-card__meta">
          {scheduleDate && (
            <span className="tp-task-card__meta-item" title="Scheduled date">
              {shortDate(scheduleDate)}
            </span>
          )}
          <span className="tp-task-card__meta-item" title="Linked issues">
            {task.linkedIssueIds.length} issue{task.linkedIssueIds.length !== 1 ? 's' : ''}
          </span>
          {assigneeName && (
            <span className="tp-task-card__meta-item" title="Assignee">
              {assigneeName}
            </span>
          )}
        </div>

        {/* Row 4: Status badge + actions */}
        <div className="tp-task-card__footer">
          <span
            className="tp-badge"
            style={{ background: statusColor, color: '#fff' }}
          >
            {task.status}
          </span>

          <div className="tp-task-card__actions">
            <button className="tp-btn tp-btn--sm" onClick={onEdit}>
              Edit
            </button>
            {onEmail && (
              <button className="tp-btn tp-btn--sm" onClick={onEmail}>
                Email
              </button>
            )}
            {!isTerminal && (
              <>
                <button
                  className="tp-btn tp-btn--sm tp-btn--success"
                  onClick={onComplete}
                >
                  Complete
                </button>
                <button
                  className="tp-btn tp-btn--sm tp-btn--danger"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
